import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ButtonGroup,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

const NAV_SECTIONS = [
  { key: "nav.about", id: "about" },
  { key: "nav.skills", id: "skills" },
  { key: "nav.projects", id: "projects" },
  { key: "nav.journey", id: "journey" },
  { key: "nav.contact", id: "contact" },
];

function NavBar() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observers = NAV_SECTIONS.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: "-64px 0px -50% 0px", threshold: 0 }
      );
      observer.observe(el);
      return observer;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setDrawerOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled ? "rgba(9,9,11,0.92)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled
            ? "1px solid rgba(255,255,255,0.07)"
            : "none",
          transition: "background 0.4s ease, border-bottom 0.4s ease",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            px: { xs: 2, md: 4 },
            minHeight: { xs: 56, md: 64 },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              letterSpacing: 1,
              background: "linear-gradient(45deg, #f5f5f5, #818cf8)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            TK
          </Typography>

          {!isMobile && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {NAV_SECTIONS.map(({ key, id }) => {
                const isActive = activeSection === id;
                return (
                  <Button
                    key={id}
                    onClick={() => scrollTo(id)}
                    sx={{
                      color: isActive ? "#fff" : "rgba(255,255,255,0.65)",
                      fontWeight: isActive ? 600 : 500,
                      fontSize: "0.875rem",
                      px: 1.5,
                      position: "relative",
                      bgcolor: isActive
                        ? "rgba(99,102,241,0.1)"
                        : "transparent",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 4,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        opacity: isActive ? 1 : 0,
                        transition: "opacity 0.3s ease",
                      },
                      "&:hover": {
                        color: "primary.main",
                        bgcolor: isActive
                          ? "rgba(99,102,241,0.15)"
                          : "rgba(99,102,241,0.08)",
                      },
                      transition: "color 0.2s, background 0.2s",
                    }}
                  >
                    {t(key)}
                  </Button>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ButtonGroup
              size="small"
              sx={{
                "& .MuiButton-root": {
                  minWidth: 36,
                  px: 1,
                  py: 0.5,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  borderColor: "rgba(255,255,255,0.12) !important",
                  transition: "all 0.2s",
                },
              }}
            >
              {["en", "de"].map((lang) => (
                <Button
                  key={lang}
                  onClick={() => i18n.changeLanguage(lang)}
                  sx={{
                    color:
                      i18n.language === lang
                        ? "#fff"
                        : "rgba(255,255,255,0.35)",
                    bgcolor:
                      i18n.language === lang
                        ? "rgba(99,102,241,0.45)"
                        : "transparent",
                    "&:hover": {
                      bgcolor:
                        i18n.language === lang
                          ? "rgba(99,102,241,0.6)"
                          : "rgba(255,255,255,0.07)",
                    },
                  }}
                >
                  {lang.toUpperCase()}
                </Button>
              ))}
            </ButtonGroup>

            {isMobile && (
              <IconButton
                size="small"
                onClick={() => setDrawerOpen(true)}
                sx={{ color: "text.primary" }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 220,
            bgcolor: "#09090b",
            borderLeft: "1px solid rgba(99,102,241,0.15)",
            pt: 1,
          },
        }}
      >
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", px: 1, pb: 1 }}
        >
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: "text.secondary" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List dense>
          {NAV_SECTIONS.map(({ key, id }) => {
            const isActive = activeSection === id;
            return (
              <ListItem key={id} disablePadding>
                <ListItemButton
                  onClick={() => scrollTo(id)}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    bgcolor: isActive ? "rgba(99,102,241,0.1)" : "transparent",
                  }}
                >
                  <ListItemText
                    primary={t(key)}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontWeight: isActive ? 600 : 500,
                        fontSize: "0.95rem",
                        color: isActive ? "#fff" : "inherit",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </>
  );
}

export default NavBar;
